import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  decideAccessRequest,
  listAccessRequests,
  listPortalUsers,
  type AccessRequest,
  type PortalUser,
} from "@/api/portal";
import { useIAMAuth } from "@/auth/iam";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";
import { personLabel, roleLabel, sameId } from "@/pages/portal/people";

/**
 * Client Portal — the PHI access-approval queue.
 *
 * **This is the only route to patient-data access** (§4.3: no unilateral PHI
 * grants), for ClientAdmins as much as anyone. A role that can read PHI cannot
 * be handed out directly, even by an admin; it is requested, approved by a
 * *second* person, and granted for a bounded time that lapses on its own.
 *
 * Three things this screen owes the approver, because approving here is the act
 * that exposes patient data:
 *
 * 1. **Say what is actually being granted.** Not "approve request #4" — *who*
 *    gets *which* role, and that it lets them read patient data. An approver who
 *    doesn't understand the grant isn't a control, they're a rubber stamp, and a
 *    rubber stamp is worse than no gate at all because it looks like one.
 * 2. **Show the justification.** It is the entire reason the requester was made
 *    to write one. A queue that hides it turns a two-person rule into a
 *    two-click rule.
 * 3. **Approver ≠ requester, said in plain words.** Your own requests stay on the
 *    list, so an admin sees what they asked for, but they carry no Approve button
 *    and say another lab admin approves them (docs#239 note 19795 item 45). Clicking
 *    Approve used to reach IAM and come back as a 503 wrapping its 400. The BFF still
 *    refuses a self-approval that gets past the page, in the same words, and IAM
 *    enforces it under both.
 *
 * People and roles are named, not shown as ids (item 46): the person from the lab's
 * user list (name, else email, else username), the role without its IAM prefix.
 */
export function PortalApprovalsPage(): JSX.Element {
  const { organizationId, user: me } = useIAMAuth();
  const queryClient = useQueryClient();

  const requests = useQuery({
    queryKey: ["portal-access-requests"],
    queryFn: listAccessRequests,
    retry: false,
  });
  // Shared with the users page (same key). A failed read costs only the names: the card
  // then falls back to the ids it carried before.
  const users = useQuery({
    queryKey: ["portal-users"],
    queryFn: listPortalUsers,
    retry: false,
  });
  const byId = new Map<string, PortalUser>(
    (users.data?.users ?? []).map((u) => [u.id.toLowerCase(), u]),
  );
  const find = (id: string | null): PortalUser | undefined =>
    id === null ? undefined : byId.get(id.toLowerCase());

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approve" | "deny" }) =>
      decideAccessRequest(id, decision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["portal-access-requests"] });
      // A granted membership changes what the users screen shows.
      void queryClient.invalidateQueries({ queryKey: ["portal-users"] });
    },
  });

  return (
    <AppLayout
      breadcrumbs={[{ label: t("portal.title"), to: "/portal" }, { label: t("portal.approvals") }]}
    >
      <Group justify="space-between" align="baseline">
        <Title order={1}>{t("portal.approvals")}</Title>
        <Text c="dimmed" size="sm">
          {t("portal.organization")}: <Code>{organizationId}</Code>
        </Text>
      </Group>
      <Text c="dimmed" mt="xs" size="sm">
        {t("portal.approvals_subtitle")}
      </Text>

      {decide.isError ? (
        <Alert color="red" mt="md" title={t("portal.approval_refused")}>
          <Text size="sm">
            {decide.error instanceof Error
              ? decide.error.message
              : t("authoring.error_generic")}
          </Text>
        </Alert>
      ) : null}

      {requests.isPending ? (
        <TableSkeleton label={t("portal.approvals_loading")} />
      ) : requests.isError ? (
        <Alert color="red" mt="md" title={t("portal.approvals_unavailable")}>
          <Text size="sm">
            {requests.error instanceof Error
              ? requests.error.message
              : t("authoring.error_generic")}
          </Text>
        </Alert>
      ) : requests.data.length === 0 ? (
        <Text c="dimmed" mt="md">
          {t("portal.approvals_empty")}
        </Text>
      ) : (
        <Stack mt="md" gap="sm">
          {requests.data.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              target={personLabel(find(request.target_user_id), request.target_user_id)}
              requester={
                request.requested_by === null
                  ? null
                  : personLabel(find(request.requested_by), request.requested_by)
              }
              mine={sameId(request.requested_by, me?.sub)}
              busy={decide.isPending}
              onDecide={(decision) => decide.mutate({ id: request.id, decision })}
            />
          ))}
        </Stack>
      )}
    </AppLayout>
  );
}

function RequestCard({
  request,
  target,
  requester,
  mine,
  busy,
  onDecide,
}: {
  request: AccessRequest;
  /** Who gets the role, by name where the lab's user list has one. */
  target: string;
  requester: string | null;
  /** The caller made this request, so they cannot approve it. */
  mine: boolean;
  busy: boolean;
  onDecide: (decision: "approve" | "deny") => void;
}): JSX.Element {
  return (
    <Card withBorder padding="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            {/* WHO gets WHICH role — by NAME, not a GUID. The approver reads this. */}
            <Text fw={600}>
              {roleLabel(request.group_name, request.group_id)} → {target}
            </Text>
            {requester === null ? null : (
              <Text size="xs" c="dimmed">
                {t("portal.approval_requested_by")} {mine ? t("portal.approval_you") : requester}
              </Text>
            )}
            <Group gap="xs">
              <Badge color="red" variant="light">
                {t("portal.approval_grants_phi")}
              </Badge>
              <Badge color="gray" variant="light">
                {request.requested_duration_days === null
                  ? t("portal.approval_duration_default")
                  : `${request.requested_duration_days} ${t("portal.approval_days")}`}
              </Badge>
              {request.status === null ? null : (
                <Badge variant="light">{request.status}</Badge>
              )}
            </Group>
          </Stack>
          <Group gap="xs" wrap="nowrap">
            <Button
              size="xs"
              color="red"
              variant="light"
              disabled={busy}
              onClick={() => onDecide("deny")}
            >
              {t("portal.approval_deny")}
            </Button>
            {mine ? null : (
              <Button size="xs" disabled={busy} onClick={() => onDecide("approve")}>
                {t("portal.approval_approve")}
              </Button>
            )}
          </Group>
        </Group>

        {mine ? (
          <Text size="sm" c="dimmed">
            {t("portal.approval_own_request")}
          </Text>
        ) : null}

        {/* The justification is the whole point of the two-person rule. */}
        {request.justification === null ? (
          <Text size="sm" c="dimmed">
            {t("portal.approval_no_justification")}
          </Text>
        ) : (
          <Text size="sm">{request.justification}</Text>
        )}
      </Stack>
    </Card>
  );
}
